import React, { useEffect, useState } from "react";
import api from "../../../api/axios";

/* Rate-wise GST summary for filing. Prices are tax-inclusive; the backend
   back-calculates tax from the per-line snapshots taken at order time, so
   this report is stable even after TaxMaster rates change. */

/* Every amount in the report is an integer number of PAISE. */
const rupees = (paise) => (Number(paise) || 0) / 100;

const fmt = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* first + last day of the previous month — the range you file for */
const prevMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  const ymd = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  return { from: ymd(first), to: ymd(last) };
};

const LABEL = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  color: "var(--mly-text-muted)",
  marginBottom: 6,
  textTransform: "uppercase",
};
const INPUT = {
  padding: "9px 12px",
  borderRadius: 10,
  border: "1.5px solid var(--mly-border)",
  fontSize: 14,
  outline: "none",
  background: "var(--mly-surface)",
};

const GstReport = () => {
  const initial = prevMonthRange();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (f = from, t = to) => {
    setLoading(true);
    setError("");
    try {
      /* from/to are required YYYY-MM-DD days on the store's clock; without
         both the route answers 400 { code: "invalid_range", message }. `to`
         names a whole day and is inclusive of it. */
      const res = await api.get(
        `/admin/orders/gst-report?from=${f}&to=${t}`
      );
      setData(res.data);
    } catch (e) {
      setError(
        e.response?.data?.message ||
          e.response?.data?.code ||
          "Failed to load the GST report"
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(initial.from, initial.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCsv = () => {
    if (!data) return;
    const lines = [
      `GST Report,${data.storeName || ""}`,
      `GSTIN,${data.gstin || "(set in Store Settings)"}`,
      `Period,${data.from} to ${data.to}`,
      `Orders,${data.orderCount}`,
      "",
      "GST Rate (%),Taxable Value,CGST,SGST,Total Tax,Gross (incl. tax)",
      ...data.rows.map(
        (r) =>
          `${r.rate},${rupees(r.taxablePaise).toFixed(2)},${rupees(r.cgstPaise).toFixed(
            2
          )},${rupees(r.sgstPaise).toFixed(2)},${rupees(r.taxPaise).toFixed(
            2
          )},${rupees(r.grossPaise).toFixed(2)}`
      ),
      "",
      `TOTAL,${rupees(data.totals.taxablePaise).toFixed(2)},${rupees(
        data.totals.cgstPaise
      ).toFixed(2)},${rupees(data.totals.sgstPaise).toFixed(2)},${rupees(
        data.totals.taxPaise
      ).toFixed(2)},${rupees(data.totals.itemsGrossPaise).toFixed(2)}`,
      `Delivery charges (no GST),${rupees(data.totals.shippingPaise).toFixed(2)}`,
      `Grand gross incl. delivery,${rupees(data.totals.grossPaise).toFixed(2)}`,
      "",
      `Note,"${data.note || ""}"`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `gst-report-${data.from}-to-${data.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="container-fluid">
      <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
          <div>
            <h5 style={{ margin: 0, fontWeight: 700 }}>GST Report</h5>
            <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
              Rate-wise summary for filing. Prices are tax-inclusive — GST is
              back-calculated from each order's snapshot.
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <div>
              <label style={LABEL}>From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={INPUT} />
            </div>
            <div>
              <label style={LABEL}>To</label>
              <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} style={INPUT} />
            </div>
            <button className="btn btn-primary btn-sm" style={{ height: 40 }} onClick={() => load()}>
              Run
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ height: 40 }}
              onClick={downloadCsv}
              disabled={!data || !data.rows?.length}
            >
              ⬇ CSV
            </button>
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">Loading…</div>
          ) : error ? (
            <div className="text-danger text-center py-4">{error}</div>
          ) : !data ? null : (
            <>
              {/* header strip */}
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
                {[
                  { k: "GSTIN", v: data.gstin || "— set in Store Settings" },
                  { k: "Period", v: `${data.from} → ${data.to}` },
                  { k: "Orders", v: data.orderCount },
                  { k: "Total tax", v: `₹${fmt(rupees(data.totals.taxPaise))}` },
                ].map((c) => (
                  <div key={c.k} style={{
                    background: "var(--mly-surface-2)", border: "1px solid var(--mly-border)",
                    borderRadius: 12, padding: "10px 16px", minWidth: 130,
                  }}>
                    <div style={{ fontSize: 11, color: "var(--mly-text-muted)", fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}>{c.k}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--mly-heading)", marginTop: 2 }}>{c.v}</div>
                  </div>
                ))}
              </div>

              <div style={{ overflowX: "auto" }}>
                <table className="display table table-bordered">
                  <thead>
                    <tr>
                      <th>GST Rate</th>
                      <th style={{ textAlign: "right" }}>Taxable Value</th>
                      <th style={{ textAlign: "right" }}>CGST</th>
                      <th style={{ textAlign: "right" }}>SGST</th>
                      <th style={{ textAlign: "right" }}>Total Tax</th>
                      <th style={{ textAlign: "right" }}>Gross (incl. tax)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: "center", padding: "36px 0", color: "var(--mly-text-muted)" }}>
                          No orders with GST snapshots in this period.
                        </td>
                      </tr>
                    ) : (
                      data.rows.map((r) => (
                        <tr key={r.rate}>
                          <td>{Number(r.rate) === 0 ? "0% (nil-rated / exempt)" : `${r.rate}%`}</td>
                          <td style={{ textAlign: "right" }}>₹{fmt(rupees(r.taxablePaise))}</td>
                          <td style={{ textAlign: "right" }}>₹{fmt(rupees(r.cgstPaise))}</td>
                          <td style={{ textAlign: "right" }}>₹{fmt(rupees(r.sgstPaise))}</td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>₹{fmt(rupees(r.taxPaise))}</td>
                          <td style={{ textAlign: "right" }}>₹{fmt(rupees(r.grossPaise))}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {data.rows.length > 0 && (
                    <tfoot>
                      <tr style={{ fontWeight: 700, background: "var(--mly-surface-2)" }}>
                        <td>Total</td>
                        <td style={{ textAlign: "right" }}>₹{fmt(rupees(data.totals.taxablePaise))}</td>
                        <td style={{ textAlign: "right" }}>₹{fmt(rupees(data.totals.cgstPaise))}</td>
                        <td style={{ textAlign: "right" }}>₹{fmt(rupees(data.totals.sgstPaise))}</td>
                        <td style={{ textAlign: "right" }}>₹{fmt(rupees(data.totals.taxPaise))}</td>
                        <td style={{ textAlign: "right" }}>₹{fmt(rupees(data.totals.itemsGrossPaise))}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              <div style={{ fontSize: 12, color: "var(--mly-text-muted)", marginTop: 12 }}>
                Delivery charges collected (no GST applied): ₹{fmt(rupees(data.totals.shippingPaise))} ·
                Grand gross incl. delivery: ₹{fmt(rupees(data.totals.grossPaise))}
                <br />
                {data.note}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GstReport;
